import React, { JSX } from 'react';
import SEO from 'components/seo';
import { Box, Circle, Container, Heading, HStack, SimpleGrid, VStack, Icon } from '@chakra-ui/react';
import { Text } from '@chakra-ui/react';
import Breadcrumb from 'components/breadcrumb';
import { ROUTES } from 'services/shared/routerService';
import { DatabaseIcon, EyeIcon, GlobeIcon, LockIcon, UsersIcon } from 'lucide-react';

interface PropsInterface {
	children?: React.ReactNode;
}

function PrivacyPolicies(props: PropsInterface): JSX.Element {
	return (
		<Box>
			<SEO
				title="Imagemotion: Privacy Policies"
				description="Create viral ads with ai for e-commerce and dropshipping. Scale with winning video and image ads."
			/>
			<Box mb={'40'}>
				<Box as="main" bg="white">
					<Box
						as="section"
						pt="12"
						pb="12"
						px="4"
						bgGradient="to-br"
						gradientFrom="blue.600"
						gradientTo="#2B6CB0"
					>
						<Container maxW="3xl" textAlign="center">
							<Heading
								as="h1"
								fontSize={{ base: '3xl', md: '4xl' }}
								color="white"
								fontWeight="extrabold"
								mb="3"
							>
								Privacy Policy
							</Heading>
							<Text fontSize="md" color="whiteAlpha.800" maxW="xl" mx="auto">
								Your privacy is important to us. This policy explains how we collect, use, and protect
								your information.
							</Text>
							<Text fontSize="xs" color="whiteAlpha.600" mt="3">
								Last updated: April 2026
							</Text>
						</Container>
					</Box>

					<Box as="section" py="12" px="4" bg="gray.50">
						<Container maxW="3xl">
							<SimpleGrid columns={{ base: 1, sm: 3 }} gap="5">
								{[
									{ icon: LockIcon, title: 'Secure', desc: 'Your data is encrypted' },
									{ icon: EyeIcon, title: 'Transparent', desc: 'Clear data collection' },
									{ icon: UsersIcon, title: 'No Selling', desc: 'We never sell data' },
								].map((item, i) => (
									<VStack
										key={i}
										bg="white"
										rounded="xl"
										gap={0}
										p="5"
										shadow="sm"
										borderWidth="1px"
										borderColor="gray.200"
										textAlign="center"
									>
										<Circle size="10" bg="blue.50" mb="4">
											<Icon as={item.icon} boxSize={4} color="blue.600" />
										</Circle>
										<Heading as="h3" fontSize="sm" fontWeight="bold" mb={0}>
											{item.title}
										</Heading>
										<Text fontSize="xs" color="gray.600" mt={0}>
											{item.desc}
										</Text>
									</VStack>
								))}
							</SimpleGrid>
						</Container>
					</Box>

					<Box as="section" py="12" px="4">
						<Container maxW="2xl">
							<Breadcrumb
								navigation={[
									{ label: 'Home', href: ROUTES.HOME },
									{ label: 'Legal' },
									{ label: 'Privacy Policies', href: ROUTES.PRIVACYPOLICIES },
								]}
							/>
							<VStack align="start" gap="6" mt={12}>
								<Box>
									<HStack gap="2" mb="3">
										<Icon as={DatabaseIcon} boxSize={6} color="var(--chakra-colors-blue-600)" />
										<Heading as="h2" fontSize="xl">
											Information We Collect
										</Heading>
									</HStack>
									<Text color="gray.600" mb="4" fontSize="sm">
										We collect information you provide directly to us, such as when you create an
										account. This may include:
									</Text>
									<VStack align="start" gap="2" pl="2">
										{[
											{ label: 'Name and email address' },
											{ label: 'Payment information' },
											{
												label: 'Images and content you upload',
												subItems: [
													'If you upload images containing faces, we process this data solely as a visual reference for the generative AI process.',
													'Usage: We do not use this data for identification purposes.',
													'Control: You may delete this data at any time via the app.',
												],
											},
											{ label: 'Usage data and preferences' },
										].map((item, i) => (
											<VStack key={i} align="start" gap={1} w="full">
												<HStack align="start" gap="3">
													<Circle size="1.5" bg="blue.500" mt="1.5" />
													<Text fontSize="sm" color="gray.600">
														{item.label}
													</Text>
												</HStack>
												{item.subItems && (
													<VStack align="start" gap={1} pl={6} mt={1}>
														{item.subItems.map((sub, j) => (
															<HStack key={j} align="start" gap="2">
																<Circle size="1" bg="gray.400" mt="1.5" />
																<Text fontSize="xs" color="gray.500">
																	{sub}
																</Text>
															</HStack>
														))}
													</VStack>
												)}
											</VStack>
										))}
									</VStack>
								</Box>

								<Box>
									<HStack gap="2" mb="3">
										<Icon as={GlobeIcon} boxSize={6} color="var(--chakra-colors-blue-600)" />
										<Heading as="h2" fontSize="xl">
											How We Use Information
										</Heading>
									</HStack>
									<VStack align="start" gap="2" pl="2">
										{[
											'Provide and improve our services',
											'Process transactions',
											'Send technical notices',
											'Develop new products',
										].map((item, i) => (
											<HStack key={i} align="start" gap="3">
												<Circle size="1.5" bg="cyan.500" mt="1.5" />
												<Text fontSize="sm" color="gray.600">
													{item}
												</Text>
											</HStack>
										))}
									</VStack>
								</Box>

								<Box>
									<Heading as="h2" fontSize="xl" mb="3">
										Data Security
									</Heading>
									<Text fontSize="sm" color="gray.600">
										We take reasonable measures to help protect information about you from loss,
										theft, and unauthorized access. All data is encrypted in transit using
										industry-standard protocols.
									</Text>
								</Box>

								<Box>
									<Heading as="h2" fontSize="xl" mb="3">
										Your Rights
									</Heading>
									<Text fontSize="sm" color="gray.600">
										You have the right to access, correct, or delete your personal information at
										any time. Contact us at {process.env.NEXT_PUBLIC_MAIL}.
									</Text>
								</Box>

								<Box w="full" bg="gray.50" p="5" rounded="xl">
									<Heading as="h3" fontSize="md" mb="1">
										Contact Us
									</Heading>
									<Text fontWeight="semibold" fontSize="sm">
										{process.env.NEXT_PUBLIC_APP_NAME}
									</Text>
									<Text fontSize="xs" color="gray.600">
										{process.env.NEXT_PUBLIC_MAIL}
									</Text>
								</Box>
							</VStack>
						</Container>
					</Box>
				</Box>
			</Box>
		</Box>
	);
}
export default PrivacyPolicies;
